import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination } from 'swiper';
import { Swiper as SwiperRef } from 'swiper/types';
import { dateFormatRange } from '../../../../helpers/dateTimes';
import { CallbackTranslation } from '../../../../locale/localizationContext';
import { useLanguage } from '../../../../locale/useLanguage';

import 'swiper/css'; // core Swiper
import 'swiper/css/pagination';

import './DateSlider.css';

type DateSliderProps = {
  dates: Dayjs[];
  date: string | null;
  isDisabled?: boolean;
  onDateChange: (date: Dayjs) => void;
};

SwiperCore.use([Pagination]);

export const DateSlider: React.FC<DateSliderProps> = ({
  dates,
  date,
  onDateChange,
  isDisabled,
}) => {
  const [t] = useLanguage();
  const swiperRef = useRef<SwiperRef | null>(null);

  const isChangedProgrammatically = useRef<boolean>(false);

  const onSliderDateChange = useCallback(
    (swiper: SwiperRef) => {
      if (isChangedProgrammatically.current) {
        isChangedProgrammatically.current = false;
      } else {
        const newIndex = swiper.activeIndex;
        onDateChange(dates[newIndex]);
      }
    },
    [dates, onDateChange]
  );

  useEffect(() => {
    if (date) {
      const indexToSet = dates.findIndex((d) => {
        return d.format(dateFormatRange) === date;
      });
      if (indexToSet >= 0 && indexToSet !== swiperRef.current?.activeIndex) {
        isChangedProgrammatically.current = true;
        swiperRef.current?.slideTo(indexToSet);
      }
    }
  }, [dates, date]);

  const setSwiperRef = useCallback((swiper: SwiperRef) => {
    swiperRef.current = swiper;
  }, []);

  return (
    <Swiper
      slidesPerView={'auto'}
      centeredSlides={true}
      onSlideChange={onSliderDateChange}
      onSwiper={setSwiperRef}
      grabCursor
      slideToClickedSlide
      className="date-slider--swiper"
      allowTouchMove={!isDisabled}
      noSwiping={isDisabled}
      noSwipingClass="swiper-slide"
      pagination={{
        clickable: true,
        dynamicBullets: true,
      }}
    >
      {dates.map((d, i) => {
        var { monthDate, stringDay } = getLabels(d, t);

        return (
          <SwiperSlide key={i}>
            {({ isActive }) => (
              <div
                className={`date-slider--carousel-container ${
                  isActive ? 'date-slider--slide-active' : ''
                }`}
              >
                <div className="date-slider--carousel-card">
                  <h3 className="no-highlight date-slider--card-title">
                    {monthDate.toUpperCase()}
                  </h3>
                  <p className="no-highlight date-slider--data-description">
                    {stringDay}
                  </p>
                </div>
              </div>
            )}
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

export function getLabels(d: dayjs.Dayjs, t: CallbackTranslation) {
  const monthDate = `${d.format('DD MMM')}`;
  const todayWithNoTime = dayjs(dayjs().format(dateFormatRange));
  const dayDifferenceFromToday = d.diff(todayWithNoTime, 'day');

  let stringDay: string;

  if (dayDifferenceFromToday === 0) {
    stringDay = t('chooseDeliveryTime.today');
  } else if (dayDifferenceFromToday === 1) {
    stringDay = t('chooseDeliveryTime.tomorrow');
  } else {
    stringDay = d.format('dddd');
  }
  return { monthDate, stringDay };
}
